import React, { useState } from 'react'
import Image from 'next/image'
import Script from 'next/script'
import styles from './ContactPageForm.module.scss'
import { getAllCountries, getStatesByCountryName } from 'countries-states-data'

import Cookies from 'js-cookie'
import Select, { Options, SingleValue } from 'react-select'
import CustomSelect, { Value } from '../../components/customSelect/CustomSelect'
import { widont } from '../../utils/typography'
import { useGlobalContentContext } from '../../context/GlobalContentContext'

type ContactProps = {}

const Contact = (props: ContactProps) => {
  const { data } = useGlobalContentContext()

  const hutk = Cookies.get('hubspotutk')

  const [formSubmitted, setFormSubmitted] = useState<boolean>(false)
  const [messageRows, setMessageRows] = useState<number>(2)

  const [formErrors, setFormErrors] = useState<string[]>([])

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    // Stop the form from submitting and refreshing the page.
    event.preventDefault()

    const formdata = new FormData(event.target as HTMLFormElement)

    const target = event.target as typeof event.target & {
      firstname: { value: string }
      lastname: { value: string }
      company: { value: string }
      jobtitle: { value: string }
      email: { value: string }
      phone: { value: string }
      country: { value: string }
      state: { value: string }
      zip: { value: string }
      segment: { value: string }
      employees: { value: string }
      message: { value: string }
    }

    // Get data from the form.
    const data = {
      firstname: target.firstname.value,
      lastname: target.lastname.value,
      company: target.company.value,
      jobtitle: target.jobtitle.value,
      email: target.email.value,
      phone: target.phone.value,
      country: countryValue,
      state: stateValue,
      zip: target.zip.value,
      segment: formdata.getAll('segment').join(';'),
      employees: target.employees.value,
      message: target.message.value,
      hutk: hutk ? hutk : '',
    }

    // Send the data to the server in JSON format.
    const JSONdata = JSON.stringify(data)

    // API endpoint where we send form data.
    const endpoint = '/api/contactform'

    // Form the request for sending data to the server.
    const options = {
      // The method is POST because we are sending data.
      method: 'POST',
      // Tell the server we're sending JSON.
      headers: {
        'Content-Type': 'application/json',
      },
      // Body of the request is the JSON data we created above.
      body: JSONdata,
    }

    // Send the form data to our forms API on Vercel and get a response.
    const response = await fetch(endpoint, options)

    // Get the response data from server as JSON.
    // If server returns the name submitted, that means the form works.
    const result = await response.json()

    if (result.data) {
      setFormErrors(Array.from(result.data))
    } else {
      setFormSubmitted(true)
    }
  }

  const countryOptions = getAllCountries()
    .map((country: { name: string }) => {
      return { value: country.name, label: country.name }
    })
    .sort((a: { label: string }, b: { label: string }) => {
      if (b.label == 'United States') {
        return 1
      } else if (a.label == 'United States') {
        return -1
      } else {
        return a.label.toLowerCase() < b.label.toLowerCase()
          ? -1
          : a.label.toLowerCase() > b.label.toLowerCase()
          ? 1
          : 0
      }
    })

  const [countryValue, setCountryValue] = useState<string>(
    countryOptions[0].value
  )

  const handleCountryChange = (value: Value) => {
    setCountryValue(value.value)
    setStateValue(undefined)
  }

  const stateOptions = getStatesByCountryName(countryValue).map(
    (state: { state_code: string; name: string }) => {
      return { value: state.state_code, label: state.name }
    }
  )
  const [stateValue, setStateValue] = useState<string | undefined>(undefined)

  const handleStateChange = (value: Value) => {
    setStateValue(value.value)
  }

  return (
    <section>
      <div className={styles.container}>
        <div id="my-hubspot-form" />

        <div className={styles.errors}>
          <ul>
            {formErrors.map((error, index) => {
              return <li key={`error-${index}`}>{error}</li>
            })}
          </ul>
        </div>
        <div className={styles.form}>
          {!formSubmitted ? (
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroupContainer}>
                <div className={styles.formGroup}>
                  <input
                    type="text"
                    id="firstname-c4f21a52-7719-4654-8cb5-e93c241139b0"
                    name="firstname"
                    placeholder="First Name*"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <input
                    type="text"
                    id="lastname-c4f21a52-7719-4654-8cb5-e93c241139b0"
                    name="lastname"
                    placeholder="Last Name*"
                    required
                  />
                </div>
              </div>
              <div className={styles.formGroupContainer}>
                <div className={styles.formGroup}>
                  <input
                    type="text"
                    id="company-c4f21a52-7719-4654-8cb5-e93c241139b0"
                    name="company"
                    placeholder="Company Name*"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <input
                    type="text"
                    id="jobtitle-c4f21a52-7719-4654-8cb5-e93c241139b0"
                    name="jobtitle"
                    placeholder="Role*"
                    required
                  />
                </div>
              </div>
              <div className={styles.formGroupContainer}>
                <div className={styles.formGroup}>
                  <input
                    type="email"
                    id="email-c4f21a52-7719-4654-8cb5-e93c241139b0"
                    name="email"
                    placeholder="Email*"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <input
                    type="text"
                    id="phone-c4f21a52-7719-4654-8cb5-e93c241139b0"
                    name="phone"
                    placeholder="Phone"
                  />
                </div>
              </div>
              <div className={styles.formGroupContainer}>
                <div className={styles.formGroup}>
                  <div className={styles.select}>
                    <CustomSelect
                      id="country-c4f21a52-7719-4654-8cb5-e93c241139b0"
                      name="country"
                      onChange={handleCountryChange}
                      options={countryOptions}
                      placeholder="Please Select Country*"
                      placeholderColor={'#000'}
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <div className={styles.select}>
                    <CustomSelect
                      id="state-c4f21a52-7719-4654-8cb5-e93c241139b0"
                      name="state"
                      onChange={handleStateChange}
                      options={stateOptions}
                      placeholder="Please Select State*"
                      placeholderColor={'#000'}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formGroupContainer}>
                <div className={styles.formGroupHalf}>
                  <input
                    type="text"
                    id="zip-c4f21a52-7719-4654-8cb5-e93c241139b0"
                    name="zip"
                    placeholder="Zip Code*"
                    required
                  />
                </div>
              </div>
              <div className={styles.formGroupContainer}>
                <div className={styles.formCheckboxGroup}>
                  <div>Segment(s) your Company belongs to*</div>
                  <div className={styles.checkboxContainer}>
                    <label htmlFor="segment0-c4f21a52-7719-4654-8cb5-e93c241139b0">
                      <input
                        type="checkbox"
                        id="segment0-c4f21a52-7719-4654-8cb5-e93c241139b0"
                        name="segment"
                        value="Battery Manufacturing"
                      />
                      <div className={styles.checkmark}></div>
                      Battery Manufacturing
                    </label>
                  </div>
                  <div className={styles.checkboxContainer}>
                    <label htmlFor="segment1-c4f21a52-7719-4654-8cb5-e93c241139b0">
                      <input
                        type="checkbox"
                        id="segment1-c4f21a52-7719-4654-8cb5-e93c241139b0"
                        name="segment"
                        value="Battery Pack Assembly"
                      />
                      <div className={styles.checkmark}></div>
                      Battery Pack Assembly
                    </label>
                  </div>
                  <div className={styles.checkboxContainer}>
                    <label htmlFor="segment2-c4f21a52-7719-4654-8cb5-e93c241139b0">
                      <input
                        type="checkbox"
                        id="segment2-c4f21a52-7719-4654-8cb5-e93c241139b0"
                        name="segment"
                        value="Battery Technology"
                      />
                      <div className={styles.checkmark}></div>
                      Battery Technology
                    </label>
                  </div>
                  <div className={styles.checkboxContainer}>
                    <label htmlFor="segment3-c4f21a52-7719-4654-8cb5-e93c241139b0">
                      <input
                        type="checkbox"
                        id="segment3-c4f21a52-7719-4654-8cb5-e93c241139b0"
                        name="segment"
                        value="OEM/Battery Component Manufacturer"
                      />
                      <div className={styles.checkmark}></div>
                      OEM/Battery Component Manufacturer
                    </label>
                  </div>
                  <div className={styles.checkboxContainer}>
                    <label htmlFor="segment4-c4f21a52-7719-4654-8cb5-e93c241139b0">
                      <input
                        type="checkbox"
                        id="segment4-c4f21a52-7719-4654-8cb5-e93c241139b0"
                        name="segment"
                        value="Battery Product User"
                      />
                      <div className={styles.checkmark}></div>
                      Battery Product User
                    </label>
                  </div>
                  <div className={styles.checkboxContainer}>
                    <label htmlFor="segment5-c4f21a52-7719-4654-8cb5-e93c241139b0">
                      <input
                        type="checkbox"
                        id="segment5-c4f21a52-7719-4654-8cb5-e93c241139b0"
                        name="segment"
                        value="Media/Analysts"
                      />
                      <div className={styles.checkmark}></div>
                      Media/Analysts
                    </label>
                  </div>
                  <div className={styles.checkboxContainer}>
                    <label htmlFor="segment6-c4f21a52-7719-4654-8cb5-e93c241139b0">
                      <input
                        type="checkbox"
                        id="segment6-c4f21a52-7719-4654-8cb5-e93c241139b0"
                        name="segment"
                        value="Other"
                      />
                      <div className={styles.checkmark}></div>
                      Other
                    </label>
                  </div>
                </div>

                <div className={styles.formRadioGroup}>
                  <div className={styles.formHeading}>
                    Number of Company Employees*
                  </div>
                  <div className={styles.radioContainer}>
                    <label htmlFor="employees0-c4f21a52-7719-4654-8cb5-e93c241139b0">
                      <input
                        type="radio"
                        id="employees0-c4f21a52-7719-4654-8cb5-e93c241139b0"
                        name="employees"
                        value="1-10"
                      />
                      <div className={styles.checkmark}></div>
                      1-10
                    </label>
                  </div>
                  <div className={styles.radioContainer}>
                    <label htmlFor="employees2-c4f21a52-7719-4654-8cb5-e93c241139b0">
                      <input
                        type="radio"
                        id="employees2-c4f21a52-7719-4654-8cb5-e93c241139b0"
                        name="employees"
                        value="11-50"
                      />
                      <div className={styles.checkmark}></div>
                      11-50
                    </label>
                  </div>
                  <div className={styles.radioContainer}>
                    <label htmlFor="employees3-c4f21a52-7719-4654-8cb5-e93c241139b0">
                      <input
                        type="radio"
                        id="employees3-c4f21a52-7719-4654-8cb5-e93c241139b0"
                        name="employees"
                        value="51-100"
                      />
                      <div className={styles.checkmark}></div>
                      51-100
                    </label>
                  </div>
                  <div className={styles.radioContainer}>
                    <label htmlFor="employees4-c4f21a52-7719-4654-8cb5-e93c241139b0">
                      <input
                        type="radio"
                        id="employees4-c4f21a52-7719-4654-8cb5-e93c241139b0"
                        name="employees"
                        value="101-1,000"
                      />
                      <div className={styles.checkmark}></div>
                      101-1,000
                    </label>
                  </div>
                  <div className={styles.radioContainer}>
                    <label htmlFor="employees5-c4f21a52-7719-4654-8cb5-e93c241139b0">
                      <input
                        type="radio"
                        id="employees5-c4f21a52-7719-4654-8cb5-e93c241139b0"
                        name="employees"
                        value="1,000+"
                      />
                      <div className={styles.checkmark}></div>
                      1,000+
                    </label>
                  </div>
                </div>
              </div>
              <div className={styles.formGroupNoBorder}>
                <label htmlFor="message">How may we help you</label>
                <textarea
                  name="message"
                  id="message-c4f21a52-7719-4654-8cb5-e93c241139b0"
                  rows={messageRows}
                  onClick={() => setMessageRows(6)}
                />
              </div>

              <button type="submit">
                <span className={styles.linkArrow}>
                  <Image src="/arrow-black.svg" layout="fill" alt="" />
                </span>{' '}
                Submit
              </button>
            </form>
          ) : (
            <div className={styles.formSubmitted}>
              Thanks for your interest in Sakuu. We will get back to you
              shortly.
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default Contact
